/**
 * Rose Kit PDP — pinned side columns (desktop only).
 *
 * The CSS fallback is position: sticky, which releases an element when its OWN
 * bottom reaches the bottom of its containing block. The buy panel is ~674px
 * tall, so sticky starts moving it 674px before the buy band ends: on a short
 * window that lands while the second gallery image is still on screen, and the
 * three pinned pieces (logo, promises, panel) each let go at a different point
 * because they are different heights.
 *
 * This upgrades them to position: fixed and drives all three from ONE shared
 * measurement (the tallest pinned element) instead of each element's own:
 *
 *   1. above the pin line   -> in flow, untouched
 *   2. pinned               -> fixed at the pin line; completely still, and
 *                              all three hold for exactly the same distance
 *   3. tallest element      -> still fixed, every top pushed up by the same
 *      would pass the band     amount, so they ride the band out together
 *      bottom                  rather than covering the section below
 *
 * The shared height is what removes the stagger, and because it is the LARGEST
 * of the three, the hold lasts as long as sticky ever managed while the two
 * shorter elements can never reach the band's bottom, so nothing overlaps. If
 * this script never runs, the CSS sticky rules still give a reasonable (just
 * earlier and staggered) release.
 */
(function () {
  var band = document.querySelector('.rk-pdp-buy');
  if (!band) return;

  var PIN_TOP = 24;

  var rail = document.querySelector('.rk-pdp-rail');
  var panel = document.querySelector('.rk-pdp-panel');

  // Pin the whole right column where it fits, so the title and the Time / Skill
  // / Safety rows stay put with the buy panel rather than scrolling away above
  // it. Where it does not fit, pinning the rail would strand its bottom (the
  // CTA and the cancel line) off-screen, so fall back to the panel alone. The
  // stylesheet switches align-self on the same threshold; this reads the height
  // that results, so the two cannot disagree.
  function rightColumn() {
    if (!rail || !panel) return rail || panel;
    var railH = rail.getBoundingClientRect().height;
    return (railH + PIN_TOP <= window.innerHeight) ? rail : panel;
  }

  var targets = [
    document.querySelector('.rk-pdp-promises__logo'),
    document.querySelector('.rk-pdp-promises'),
    rightColumn()
  ].filter(Boolean).map(function (el) { return { el: el }; });
  if (!targets.length) return;

  var desktop = window.matchMedia('(min-width: 1100px)');
  var measured = false;
  var frame = null;
  var tallest = 0;   // the pinned element that reaches the band's bottom first
  var measuring = false;   // guards the ResizeObserver against our own writes
  var handedBack = false;  // true once the elements are back under CSS control

  function release(t) {
    var s = t.el.style;
    s.position = '';
    s.top = '';
    s.left = '';
    s.width = '';
    t.el.classList.remove('is-pinned');
  }

  // Read each element's in-flow slot. Position is forced static first, because
  // reading a sticky element mid-scroll would report its pinned offset, not the
  // place it actually belongs.
  function measure() {
    measuring = true;
    // is-pinned gives the rail a viewport-tall height; measuring with it on
    // would record that instead of the content height the layout actually needs.
    if (rail) rail.classList.remove('is-pinned');
    // A resize can cross the fits/does-not-fit threshold, so re-pick which
    // element carries the right column and hand back whichever one it no longer
    // is, or the abandoned element keeps the inline styles we last gave it.
    if (rail && panel) {
      [rail, panel].forEach(function (el) {
        el.style.position = ''; el.style.top = ''; el.style.left = ''; el.style.width = '';
      });
      targets[targets.length - 1].el = rightColumn();
    }
    targets.forEach(function (t) {
      t.el.style.position = 'static';
      t.el.style.top = '';
      t.el.style.left = '';
      t.el.style.width = '';
    });
    var bandTop = band.getBoundingClientRect().top;
    targets.forEach(function (t) {
      var r = t.el.getBoundingClientRect();
      t.left = r.left;
      t.width = r.width;
      t.height = r.height;
      t.offset = r.top - bandTop;   // where the element sits inside the band
    });
    tallest = targets.reduce(function (m, t) { return Math.max(m, t.height); }, 0);
    targets.forEach(function (t) { t.el.style.position = ''; });
    measured = true;
    measuring = false;
  }

  function update() {
    if (!desktop.matches) {
      // Hand everything back below the desktop breakpoint. This must NOT be
      // conditional on `measured`: a resize calls remeasure() first, which
      // clears that flag, so gating on it skipped the release and left the
      // columns stuck at position:fixed over the stacked mobile layout.
      if (!handedBack) {
        targets.forEach(release);
        measured = false;
        handedBack = true;
      }
      return;
    }
    handedBack = false;
    if (!measured) measure();

    var b = band.getBoundingClientRect();
    var vh = window.innerHeight;

    // Hold everything until the TALLEST pinned element would push past the
    // bottom of the band, then move them all by the same amount.
    //
    // Driving the hand-off from one shared height rather than each element's
    // own is what keeps the three in lockstep: sticky's per-element rule is
    // exactly why they used to let go hundreds of pixels apart. Using the
    // tallest also means it holds as long as sticky ever did, and since every
    // other element is shorter, none of them can reach the band's bottom
    // either, so nothing overlaps the section below.
    // Pinned, the rail stretches to fill the window so the panel can sit at the
    // bottom, so its pinned height (not its content height) is what reaches the
    // band's bottom and drives the shared hand-off.
    var railTarget = targets[targets.length - 1].el === rail ? targets[targets.length - 1] : null;
    var railPinnedHeight = railTarget ? Math.max(railTarget.height, vh - (PIN_TOP * 2)) : 0;
    var ridden = (PIN_TOP + Math.max(tallest, railPinnedHeight)) - b.bottom;

    targets.forEach(function (t) {
      var height = (t === railTarget) ? railPinnedHeight : t.height;
      // Taller than the viewport: pinning would cut its bottom off with no way
      // to reach it, so leave that case to normal flow.
      if (height + PIN_TOP > vh) { release(t); return; }
      // Not yet at the pin line, or the band is gone entirely.
      if (b.top + t.offset >= PIN_TOP || b.bottom <= 0) { release(t); return; }

      var s = t.el.style;
      s.position = 'fixed';
      s.top = (ridden > 0 ? PIN_TOP - ridden : PIN_TOP) + 'px';
      s.left = t.left + 'px';
      s.width = t.width + 'px';
      if (t === railTarget) t.el.classList.add('is-pinned');
    });
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () { frame = null; update(); });
  }

  function remeasure() {
    if (measuring) return;   // our own static/fixed toggling, not a real change
    measured = false;
    schedule();
  }

  // Capture phase: the page scrolls inside .page-wrapper, not the window, and
  // scroll events do not bubble.
  window.addEventListener('scroll', schedule, true);
  window.addEventListener('resize', remeasure);
  if (desktop.addEventListener) desktop.addEventListener('change', remeasure);

  // The panel's height drives the shared hand-off, and it changes with the
  // offer, the tools checkbox and the sale arm's ribbon. Observing the band is
  // not enough: the band is sized by the gallery, so it does not resize when
  // the panel does, and a stale height lets the panel overhang the band.
  if (window.ResizeObserver) {
    // Only a genuine height change may invalidate the measurement.
    //
    // The `measuring` flag is not enough on its own: measure() toggles position
    // between static and fixed, the observer fires for that AFTER the flag has
    // cleared, and the callback then triggers another measure. The two chase
    // each other every frame, which starves the scroll updates and leaves the
    // pinned column drifting on a stale offset between re-measures.
    var lastHeight = new WeakMap();
    var ro = new ResizeObserver(function (entries) {
      var changed = false;
      entries.forEach(function (entry) {
        var h = Math.round(entry.contentRect.height);
        if (lastHeight.get(entry.target) !== h) {
          lastHeight.set(entry.target, h);
          changed = true;
        }
      });
      if (changed) remeasure();
    });
    // Deliberately not the rail: its height changes are our own is-pinned
    // toggling, which would feed straight back into another measure. The panel
    // is what actually resizes with the offer and the sale arm, and the rail's
    // content height follows from it.
    [panel].concat(targets.map(function (t) { return t.el; }))
      .filter(Boolean)
      .filter(function (el) { return el !== rail; })
      .forEach(function (el) { ro.observe(el); });
  }

  update();
})();
